import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { jwtDecode } from 'jwt-decode';

const AuthContext = createContext(null);

const checkIsSuperAdmin = (user) =>
  user?.role === 'admin' ||
  user?.designation === 'SystemAdmin' ||
  user?.designation === 'SuperAdmin' ||
  user?.designation === 'Routes Super Admin';

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [user, setUser] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('careers_token');
    if (token) {
      try {
        const decoded = jwtDecode(token);
        if (decoded.exp * 1000 < Date.now()) {
          clearAuth();
        } else {
          let stored = null;
          try {
            const raw = localStorage.getItem('careers_user');
            if (raw) stored = JSON.parse(raw);
          } catch (_) {}
          const profile = stored?.user ?? stored?.data?.user ?? stored?.data ?? stored;
          setUser({ ...decoded, ...(profile && typeof profile === 'object' ? profile : {}) });
          setIsAuthenticated(true);
        }
      } catch {
        clearAuth();
      }
    } else {
      setIsAuthenticated(false);
    }
  }, []);

  const clearAuth = () => {
    localStorage.removeItem('careers_token');
    localStorage.removeItem('careers_refresh_token');
    localStorage.removeItem('careers_user');
    setUser(null);
    setIsAuthenticated(false);
  };

  const login = useCallback(({ token, refreshToken, user: userData, data: responseData }) => {
    localStorage.setItem('careers_token', token);
    if (refreshToken) localStorage.setItem('careers_refresh_token', refreshToken);
    const toStore = userData ?? responseData ?? {};
    if (Object.keys(toStore).length) localStorage.setItem('careers_user', JSON.stringify(toStore));
    const decoded = jwtDecode(token);
    const profile = userData ?? responseData?.user ?? responseData?.data ?? responseData;
    setUser({ ...decoded, ...(profile && typeof profile === 'object' ? profile : {}) });
    setIsAuthenticated(true);
  }, []);

  const logout = useCallback(() => {
    clearAuth();
  }, []);

  const updateUser = useCallback((updates) => {
    setUser((prev) => {
      const next = { ...prev, ...updates };
      // Persist back to localStorage so it survives a refresh
      try {
        const raw = localStorage.getItem('careers_user');
        const stored = raw ? JSON.parse(raw) : {};
        const profile = stored?.user ?? stored?.data?.user ?? stored?.data ?? stored;
        const updated = { ...profile, ...updates };
        // Mirror the shape that was originally stored
        if (stored?.user) localStorage.setItem('careers_user', JSON.stringify({ ...stored, user: updated }));
        else localStorage.setItem('careers_user', JSON.stringify(updated));
      } catch (_) {}
      return next;
    });
  }, []);

  const isSuperAdmin = checkIsSuperAdmin(user);
  const isClubAdmin  = user?.role === 'clubAdmin';
  const isSponsor    = user?.role === 'sponsor';
  const isTutor      = user?.role === 'tutor';

  return (
    <AuthContext.Provider value={{
      isAuthenticated,
      user,
      login,
      logout,
      updateUser,
      isSuperAdmin,
      isClubAdmin,
      isSponsor,
      isTutor,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
